import React, { useState, useEffect, Fragment, useMemo, useRef } from 'react'
import { useLocation, Prompt } from 'react-router'
import { useModal } from '@ebay/nice-modal-react'
import clsx from 'clsx'
import { CSSTransition } from 'react-transition-group'
import { User } from 'react-feather'
import { notification } from 'antd'
import { StarFilled } from '@ant-design/icons'
import { useQueryParam, BooleanParam } from 'use-query-params'
import debounce from 'lodash.debounce'

import {
  useAuthState,
  Spin,
  usePaginatedAssignmentList,
  Rate,
  Button,
  useGlobalState,
  useUserDetails,
  AcceptSubmissionModal,
} from '@pepper/ui'

import Topbar from 'components/Topbar/Topbar'
import Navbar from 'components/Navbar/Navbar'
import MobileWarning from 'components/MobileWarning'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { useSetPermissions } from 'utils/hooks/useSetPermissions'
import { createSalesforceLead } from 'utils/api/companiesLeads'

/* dashboard routes */
import TopBanner from 'components/TopBanner/TopBanner'

import Routes from './Routes'
import styles from './_.module.css'
import { getUserId } from '@pepper/utils/src/api/functions/authHelpers'

const VIEWPORT_SIZE = {
  small: 480,
  medium: 920,
}

const DEBOUNCE_TIMEOUT = 150

export type OrgModalType = {
  visible: boolean
  data: { id: string; company_name: string; invite_id: string } | null
}

const Dashboard: React.FC = () => {
  const [collapseMenu, setCollapseMenu] = useState(false)
  const [isNavHidden, setIsNavHidden] = useState(true)
  const [isMobileView, setIsMobileView] = useState(false)
  const location = useLocation()
  const { user } = useAuthState()
  const { setViewport, subscription, viewport } = useGlobalState()
  const companyId = useSelectedCompanyId()
  useSetPermissions(companyId)
  const bodyRef = useRef<HTMLElement>(null)
  const userId = getUserId()
  const { userDetails } = useUserDetails(userId)

  const acceptAssignmentModal = useModal(AcceptSubmissionModal)
  const [shouldOpenServiceSelectionModalOnLoad] = useQueryParam(
    'ossm',
    BooleanParam
  )
  const [joinOrganisationModal, setJoinOrganisationModal] =
    useState<OrgModalType>({ visible: false, data: null })

  const [showTopBanner, setShowTopBanner] = useState(false)
  const { show: openServiceSelectionModal } = useModal(
    'service-selection-drawer'
  )

  const { records: assignments } = usePaginatedAssignmentList(
    {
      stage: ['approved'],
      company_id: companyId,
      feedback: 'pending',
      fields: ['assignment_title', 'writer_allotted', 'show_pepper_creator'],
      pending_feedback_from_days: 1,
      favourite_creator: true,
    },
    undefined,
    false
  )

  useEffect(() => {
    shouldOpenServiceSelectionModalOnLoad && openServiceSelectionModal()
  }, [openServiceSelectionModal, shouldOpenServiceSelectionModalOnLoad])

  useEffect(() => {
    const showProductTour = localStorage.getItem('show-tour')

    if (showProductTour && subscription?.trialStatus) {
      createSalesforceLead({
        'Start a Free Trial Started': 'Yes',
      })
    }
  }, [subscription?.trialStatus])

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0
    }
  }, [location.pathname, location.search])

  const feedbackNotificationContent = useMemo(() => {
    if (assignments.length === 0) {
      return null
    }
    return (
      <>
        <div className={styles.feedbackRequestNotificationAssignmentTitle}>
          {assignments[0]?.assignment_title}
        </div>
        <div className={styles.feedbackRequestNotificationCreatorDetails}>
          <User size={12} />
          <span className={styles.feedbackRequestNotificationCreatorName}>
            {assignments[0]?.writer_allotted?.display_name ??
              assignments[0]?.writer_allotted?.full_name}
          </span>
        </div>
        <Button
          className={styles.feedbackRequestNotificationRatingButton}
          type="text"
        >
          <Rate
            value={0}
            character={<StarFilled style={{ fontSize: 30 }} />}
            onChange={rating => {
              notification.close('feedbackRequestNotification')
              acceptAssignmentModal.show({
                assignmentIds: assignments.map(assignment => assignment.id),
                defaultRating: rating,
                companyId,
              })
            }}
          />
        </Button>
      </>
    )
  }, [assignments, acceptAssignmentModal, companyId])

  useEffect(() => {
    if (
      assignments.length > 0 &&
      localStorage.getItem('isJustLoggedIn') === 'true'
    ) {
      notification.open({
        key: 'feedbackRequestNotification',
        duration: 0,
        message: 'How was your experience?',
        description: feedbackNotificationContent,
        placement: 'bottomRight',
        className: styles.feedbackRequestNotificationWrapper,
      })
      localStorage.setItem('isJustLoggedIn', 'false')
    }
  }, [acceptAssignmentModal, assignments, feedbackNotificationContent])

  useEffect(() => {
    const setViewportSize = (): void => {
      if (window.innerWidth <= VIEWPORT_SIZE.small) {
        setViewport('sm')
        setIsMobileView(true)
      } else if (window.innerWidth <= VIEWPORT_SIZE.medium) {
        setViewport('md')
        setIsMobileView(true)
      } else {
        setViewport('lg')
        setIsMobileView(false)
      }
      document.body.style.setProperty(
        '--navbar-height',
        `${window.innerHeight}px`
      )
    }

    setViewportSize()
    window.addEventListener(
      'resize',
      debounce(setViewportSize, DEBOUNCE_TIMEOUT)
    )
    return (): void => window.removeEventListener('resize', setViewportSize)
  }, [])

  useEffect(() => {
    // setting width of sidebar as css custom property which can we used in descending nodes for styling
    if (!collapseMenu) {
      document.body.style.setProperty('--sidebar-width', '20rem')
    } else {
      document.body.style.setProperty('--sidebar-width', '5rem')
    }
  }, [collapseMenu])

  useEffect(() => {
    if (subscription?.trialStatus === 'expired') {
      setShowTopBanner(true)
    }
  }, [subscription?.trialStatus])

  useEffect(() => {
    const inviteId = userDetails?.companies[0].invite_id
    const inviteAccepted = userDetails?.companies[0].invite_accepted
    if (companyId && !inviteAccepted && userDetails?.companies[0].invite_id) {
      const { protocol } = window.location
      const { host } = window.location
      window.location.href = `${protocol}//${host}/onboard-user?invite-code=${inviteId}#organisation-details`
    }
  }, [companyId, userDetails?.companies])

  const isInvitesRoute = location.pathname === '/invites'

  if (user.profile.onboarding_status === null && isInvitesRoute) {
    return (
      <div className={styles.spin}>
        <Spin />
      </div>
    )
  }

  if (isInvitesRoute && user.profile.onboarding_status === 'new') {
    return (
      <Fragment>
        <Prompt
          when={true} // Always render the prompt to block or allow route change
          message={() => {
            // Always allow route change, just close the feedback request notifcation
            notification.close('feedbackRequestNotification')
            return true
          }}
        />
        <nav className={styles.topbar}>
          <Topbar
            collapse={collapseMenu}
            toggleMenu={() => setCollapseMenu(!collapseMenu)}
            toggleMobileMenu={() => {
              setIsNavHidden(!isNavHidden)
            }}
            joinOrganisationModal={joinOrganisationModal}
            setJoinOrganisationModal={setJoinOrganisationModal}
          />
        </nav>
        <Routes />
      </Fragment>
    )
  }

  return (
    <Fragment>
      <Prompt
        when={true} // Always render the prompt to block or allow route change
        message={() => {
          // Always allow route change, just close the feedback request notifcation
          notification.close('feedbackRequestNotification')
          return true
        }}
      />
      {viewport === 'sm' && <MobileWarning />}
      <CSSTransition
        in={showTopBanner}
        unmountOnExit={true}
        timeout={isMobileView ? 0 : 300}
        classNames={{
          enter: styles.openBanner,
          enterActive: styles.openBannerActive,
          exit: styles.closeBanner,
          exitActive: styles.closeBannerActive,
        }}
      >
        <TopBanner
          handleClose={() => setShowTopBanner(false)}
          subscription={subscription}
        />
      </CSSTransition>
      <div className={styles.dashboard}>
        <div
          role="none"
          className={clsx({
            [styles.overlay]: !isNavHidden,
          })}
          onClick={() => {
            setIsNavHidden(true)
          }}
        ></div>
        <aside
          className={clsx(styles.sidebar, {
            [styles.mobileView]: isMobileView,
            [styles.isHidden]: isMobileView && isNavHidden,
            [styles.isShrunk]: !isMobileView && collapseMenu,
          })}
        >
          <Navbar
            collapseMenu={collapseMenu}
            setJoinOrganisationModal={setJoinOrganisationModal}
            onClose={() => setIsNavHidden(true)}
          />
        </aside>
        <main className={styles.body} ref={bodyRef}>
          <nav className={styles.topbar}>
            <Topbar
              collapse={collapseMenu}
              toggleMenu={() => setCollapseMenu(!collapseMenu)}
              toggleMobileMenu={() => setIsNavHidden(!isNavHidden)}
              joinOrganisationModal={joinOrganisationModal}
              setJoinOrganisationModal={setJoinOrganisationModal}
            />
          </nav>
          <div
            id="notification-popover-container"
            className={styles.notificationPopoverContainer}
          ></div>
          <div className={styles.routeContainer}>
            <Routes />
          </div>
        </main>
      </div>
    </Fragment>
  )
}

export default Dashboard
